import { types, flow, applySnapshot } from 'mobx-state-tree'
import Cookies from 'js-cookie'
import { API, API_URL } from '../api'

/*
 * "Hey Nic" AI chatbot data layer.
 *
 * GuidedChoice-native chat store talking to the GC AI backend under
 * `/api/agents/*` via the app's axios `API` instance. Streamed responses use
 * `fetch` + a ReadableStream because the stream endpoint is a POST
 * (`/api/agents/run/stream`), which EventSource cannot do.
 *
 * Backend contract (confirmed):
 *   GET    agents/ai-status            → { available, checkedAt, reason }
 *   GET    agents/chat                 → ChatRest[]  (full chats incl. messages)
 *   POST   agents/chat                 → ChatRest    (create)
 *   GET    agents/chat/{chatId}        → ChatRest
 *   PATCH  agents/chat/title  { chatId, title }        → ChatRest
 *   DELETE agents/chat/{chatId}        → (no body)
 *   POST   agents/run         { chatId, input }        → { output, suggestions[], ... }
 *   POST   agents/run/stream  { chatId, input }        → streamed response body
 *
 *   ChatRest    = { chatId, userId, title, createdTimestamp, deletedTimestamp, messages[] }
 *   MessageRest = { messageId, chatId, role, text, createdTimestamp, toolName,
 *                   toolCallId, validationAgent, suggestions[{id,text,...}], navLink{...} }
 *
 * run/stream is SSE (verified against the live response): frames separated by
 * blank lines, each with `id:` / `event:` metadata lines plus a `data:` line
 * carrying a JSON payload:
 *   { iteration?, feedback?, choices: [{ finishReason?, delta: {
 *       content?, statusUpdate?: { type, update }, suggestions?: [{ text }] } }] }
 * `choices` is at the TOP LEVEL (not nested under `chatCompletions`). The `data:`
 * line carries the payload; the `event:` line drives lifecycle: `final` is the
 * only successful terminator (its data holds the answer content + suggestions),
 * a handful of events (error/out_of_context_input/bad_request_event/
 * general_guardrail_event) mean failure, and every other event is interim
 * progress. Ending without a `final` means the server cut out early → error.
 * After a successful stream we reload the chat (GET agents/chat/{chatId}) for
 * the authoritative final message (ids / navLink / suggestions).
 */

const { model, optional, maybeNull, number, string, boolean, array, map, frozen } = types

const ROLE = { USER: 'user', ASSISTANT: 'assistant' }
const DISPLAY_ROLES = [ROLE.USER, ROLE.ASSISTANT]

// Names the Home panel's chat in Recents (the SBSA chats and the standalone
// route's all land in the same list). Without it the first pill tap would title
// the chat after itself.
const HOME_CHAT_TITLE = 'Home screen'

const CONTENT_FILTER_MESSAGE =
  "I'm sorry, but I can't help with that. Please rephrase your question and try again."
const GENERIC_ERROR = 'Something went wrong reaching Hey Nic. Please try again.'

// SSE `event:` type that marks a successful end. Every other status event
// (response_start, tool_processing, handoff_call, validation_start, …) is
// interim; if the stream ends on anything but this, the server cut out early.
const FINAL_EVENT = 'final'

// `event:` types the backend uses to signal failure, mapped to user-facing copy.
// Guardrail/out-of-scope turns read as a "can't help" message; the rest are
// treated as a generic failure.
const ERROR_EVENT_MESSAGES = {
  error: GENERIC_ERROR,
  bad_request_event: GENERIC_ERROR,
  out_of_context_input: CONTENT_FILTER_MESSAGE,
  general_guardrail_event: CONTENT_FILTER_MESSAGE,
}

const StatusUpdate = model('StatusUpdate', {
  update: optional(string, ''),
  type: optional(string, ''),
})

const ChatMessage = model('ChatMessage', {
  id: maybeNull(number), // messageId
  role: optional(string, ROLE.ASSISTANT),
  text: optional(string, ''),
  createdTimestamp: optional(string, ''), // ISO string
  toolName: maybeNull(string),
  toolCallId: maybeNull(string),
  validationAgent: optional(boolean, false),
  suggestions: optional(array(string), []),
  navLinkText: optional(string, ''),
  // Client-only streaming state (never sent by the server).
  loading: optional(boolean, false),
  streaming: optional(boolean, false),
  isErrorMessage: optional(boolean, false),
  currentStatusUpdate: maybeNull(StatusUpdate),
  statusUpdates: optional(array(StatusUpdate), []),
})
  .views(self => ({
    get isHuman() {
      return self.role === ROLE.USER
    },
    // Alias so the view/formatters can stay field-name agnostic.
    get timestamp() {
      return self.createdTimestamp
    },
  }))
  .actions(self => ({
    appendText(delta) {
      self.text += delta
    },
    applyStatusUpdate(statusUpdate) {
      if (!statusUpdate) return
      const normalized = {
        update: statusUpdate.update ?? '',
        type: statusUpdate.type ?? '',
      }
      self.currentStatusUpdate = normalized
      self.statusUpdates.push(normalized)
    },
    setSuggestions(list) {
      // Stream sends [{ text, ... }]; store just the display strings.
      self.suggestions.replace((list || []).map(s => s?.text ?? s).filter(Boolean))
    },
    finish() {
      self.loading = false
      self.streaming = false
      self.currentStatusUpdate = null
    },
    markError(text) {
      self.isErrorMessage = true
      self.loading = false
      self.streaming = false
      self.currentStatusUpdate = null
      if (text) self.text = text
    },
  }))

const Chat = model('Chat', {
  id: maybeNull(number), // chatId
  title: optional(string, ''),
  createdTimestamp: optional(string, ''),
  messages: optional(array(ChatMessage), []),
})
  .views(self => ({
    get humanMessages() {
      return self.messages.filter(m => m.isHuman)
    },
    get hasPreviousHumanMessages() {
      return self.humanMessages.length > 0
    },
    // A brand-new chat the user hasn't sent anything in yet.
    get isBlank() {
      return !self.hasPreviousHumanMessages
    },
    // Only user + assistant turns render in the transcript; tool/system turns
    // are internal.
    get displayMessages() {
      return self.messages.filter(m => DISPLAY_ROLES.includes(m.role))
    },
    get firstQuestion() {
      return self.humanMessages[0]?.text ?? ''
    },
    get lastMessage() {
      return self.messages[self.messages.length - 1] ?? null
    },
  }))
  .actions(self => ({
    addMessage(message) {
      self.messages.push(ChatMessage.create(message))
      return self.messages[self.messages.length - 1]
    },
    setTitle(title) {
      self.title = title
    },
    // Reconcile with a freshly-fetched snapshot WITHOUT replacing existing
    // message nodes: applySnapshot targets each node in place so its instance
    // (and any component references to it) stays alive. Only appended/removed
    // trailing messages change identity. Avoids the "read of a dead node" crash
    // when the post-stream reload lands while rows are still mounted.
    reconcileFromServer(data) {
      self.title = data.title ?? ''
      const incoming = data.messages ?? []
      incoming.forEach((m, i) => {
        if (i < self.messages.length) applySnapshot(self.messages[i], m)
        else self.messages.push(ChatMessage.create(m))
      })
      if (self.messages.length > incoming.length) self.messages.splice(incoming.length)
    },
  }))

// Availability probe: GET /api/agents/ai-status → { available, checkedAt, reason }
const AiStatus = model('AiStatus', {
  available: optional(boolean, false),
  checkedAt: optional(string, ''),
  reason: optional(string, ''),
  initialized: optional(boolean, false),
})

const ChatStore = model({
  aiStatus: optional(AiStatus, {}),
  // Recents list, normalized from the full ChatRest[] the list endpoint returns.
  chatMetadatas: optional(array(frozen()), []),
  chatMetadatasInitialized: optional(boolean, false),
  activeChat: maybeNull(Chat),
  responseLoading: optional(boolean, false),
  heyNicSidebarExpanded: optional(boolean, true),
  // ---- step-by-step advice panel (GC-18167) ---------------------------
  // Whether the desktop side panel is expanded (collapsed shows the pull tab).
  advicePanelOpen: optional(boolean, true),
  // Step path → chatId for chats the SBSA panel has created. A step only gets an
  // entry once the user actually interacts with its chatbot; until then the panel
  // shows the FE-authored opening comment and nothing is persisted.
  adviceChatIds: optional(map(number), {}),
  /*
   * The SBSA step whose chat owns the in-flight stream, or null when the running
   * stream (if any) belongs to the standalone /hey-nic chat.
   *
   * `responseLoading` alone can't tell them apart, and they must be: leaving
   * /hey-nic mid-stream deliberately keeps that stream running, so it can still
   * be going when the user walks into step-by-step advice. Locking SBSA's
   * navigation on someone else's reply would strand them on a step until it
   * finished.
   */
  adviceStreamStepPath: maybeNull(string),
  // ---- Home screen chat (GC-21520) ------------------------------------
  /*
   * The chat behind the Home screen's embedded chatbot, or null before the user
   * has said anything to it.
   *
   * Home has its own chat for the same reason each SBSA step does: `activeChat`
   * is one slot for the whole app, so the panel can't just read it. It is created
   * lazily — Home is the most-visited screen in the app, and running the
   * standalone route's entry lifecycle on every visit would pile a new (empty)
   * chat into Recents each time. Until the first send, the panel shows only the
   * FE-authored opening comment and nothing is persisted.
   */
  homeChatId: maybeNull(number),
  // Whether the in-flight stream (if any) belongs to the Home panel — the same
  // distinction `adviceStreamStepPath` draws for SBSA.
  homeStreamActive: optional(boolean, false),
})
  .volatile(() => ({
    streamController: null,
    streamBuffer: '',
    // Current SSE frame's `event:` type, and whether a `final` event was seen.
    streamEvent: null,
    streamGotFinal: false,
  }))
  .views(self => ({
    get sortedChatMetadatas() {
      return [...self.chatMetadatas].sort((a, b) => {
        const at = new Date(a.mostRecentMessageTimestamp || a.createdAt || 0).getTime()
        const bt = new Date(b.mostRecentMessageTimestamp || b.createdAt || 0).getTime()
        return bt - at
      })
    },
    get mostRecentChatMetadata() {
      return self.sortedChatMetadatas[0] ?? null
    },

    // ---- step-by-step advice panel --------------------------------------
    // A response is streaming for one of the SBSA panels — as opposed to the
    // standalone chat's, which is none of SBSA's business.
    get adviceStreaming() {
      return self.responseLoading && self.adviceStreamStepPath != null
    },
    isAdviceStepStreaming(stepPath) {
      return self.responseLoading && self.adviceStreamStepPath === stepPath
    },
    /*
     * `activeChat`, but only when it really is this step's chat.
     *
     * There is one activeChat slot for the whole app and the standalone route
     * shares it, so the panel cannot just read it — it would render whatever
     * transcript happened to be loaded. Matched on the recorded chat id rather
     * than on a "which step owns activeChat" flag, which would go stale the
     * moment /hey-nic (or the Recents sidebar) swapped the slot out.
     */
    adviceChatFor(stepPath) {
      const chatId = self.adviceChatIds.get(stepPath) ?? null
      if (chatId == null || self.activeChat?.id !== chatId) return null
      return self.activeChat
    },

    // ---- Home screen chat ------------------------------------------------
    // `activeChat`, but only when it really is the Home panel's chat (see
    // adviceChatFor — same shared-slot problem, same guard).
    get homeChat() {
      if (self.homeChatId == null || self.activeChat?.id !== self.homeChatId) return null
      return self.activeChat
    },
    // A response is streaming for the Home panel, as opposed to SBSA's or the
    // standalone route's.
    get homeStreaming() {
      return self.responseLoading && self.homeStreamActive
    },
  }))
  .actions(self => ({
    // ---- availability --------------------------------------------------
    getAiStatus: flow(function* () {
      try {
        const res = yield API.get('agents/ai-status')
        const data = res.data || {}
        // Map explicitly + coerce nulls: MST's optional(string) rejects `null`
        // (only substitutes its default for `undefined`), and `reason` comes
        // back null when the service is available — a raw spread would throw.
        self.aiStatus = {
          available: data.available === true || data.available === 'true',
          checkedAt: data.checkedAt ?? '',
          reason: data.reason ?? '',
          initialized: true,
        }
      } catch (err) {
        // Never fail app boot on the AI probe — treat as unavailable.
        self.aiStatus = { available: false, initialized: true, reason: 'unavailable' }
        console.log('[HeyNic debug] getAiStatus FAILED — treating as unavailable', err?.message)
      }
    }),

    // ---- chat list (Recents) -------------------------------------------
    getChatMetadatas: flow(function* () {
      const res = yield API.get('agents/chat')
      const chats = Array.isArray(res.data) ? res.data : []
      self.chatMetadatas = chats.map(mapChatToMetadata)
      self.chatMetadatasInitialized = true
    }),

    // ---- open / create / delete / rename -------------------------------
    openChat: flow(function* (chatId) {
      // Switching chats must abort any in-flight stream so its later chunks
      // don't write to a soon-to-be-detached message node.
      self.closeStream()
      const res = yield API.get(`agents/chat/${chatId}`)
      const snapshot = mapChatFromApi(res.data)
      // Reconcile in place when reloading the chat that's already open so
      // existing message nodes keep their identity and components still
      // referencing them never read a dead node.
      if (self.activeChat && self.activeChat.id === snapshot.id) {
        self.activeChat.reconcileFromServer(snapshot)
      } else {
        self.activeChat = snapshot
      }
      return self.activeChat
    }),

    createChat: flow(function* () {
      // Starting a new chat aborts any in-flight stream (see openChat).
      self.closeStream()
      // A new chat starts with no title and no messages; matches the documented
      // ChatRest request shape ({ title, messages }).
      const body = { title: '', messages: [] }
      const res = yield API.post('agents/chat', body)
      self.activeChat = mapChatFromApi(res.data)
      yield self.getChatMetadatas()
      return self.activeChat
    }),

    deleteChat: flow(function* (chatId) {
      // Deleting the chat that's actively streaming must abort the stream.
      if (self.activeChat?.id === chatId) self.closeStream()
      yield API.delete(`agents/chat/${chatId}`)
      self.chatMetadatas = self.chatMetadatas.filter(c => c.id !== chatId)
      if (self.activeChat?.id === chatId) self.activeChat = null
      // SBSA step chats and the Home panel's chat live in this same Recents
      // list, so deleting one from the sidebar has to drop their pointers too —
      // otherwise they keep trying to reopen a chat the server no longer has.
      self.forgetAdviceChat(chatId)
      self.forgetHomeChat(chatId)
    }),

    removeChat: flow(function* (chatId) {
      const wasActive = self.activeChat?.id === chatId
      yield self.deleteChat(chatId)
      if (wasActive) {
        const next = self.mostRecentChatMetadata
        if (next) yield self.openChat(next.id)
        else yield self.createChat()
      }
    }),

    updateTitle: flow(function* (chatId, title) {
      yield API.patch('agents/chat/title', { chatId, title })
      self.chatMetadatas = self.chatMetadatas.map(c => (c.id === chatId ? { ...c, title } : c))
      if (self.activeChat?.id === chatId) self.activeChat.setTitle(title)
    }),

    // ---- entry lifecycle -----------------------------------------------
    resolveAndOpenInitialChat: flow(function* () {
      // Returning to Hey Nic while a response is still streaming (the user
      // exited mid-stream — that's allowed, and the stream keeps running in the
      // background) drops them back into that chat so they see it finish. Never
      // create/open a different chat here: createChat/openChat abort the
      // in-flight stream, which would lose the response.
      if (self.responseLoading && self.activeChat) return self.activeChat
      if (!self.chatMetadatasInitialized) yield self.getChatMetadatas()
      const mostRecent = self.mostRecentChatMetadata
      if (mostRecent && !mostRecent.hasUserMessage) {
        return yield self.openChat(mostRecent.id)
      }
      return yield self.createChat()
    }),

    startNewChat: flow(function* () {
      if (!self.chatMetadatasInitialized) yield self.getChatMetadatas()
      const mostRecent = self.mostRecentChatMetadata
      // Reopen an existing blank chat rather than piling up another.
      if (mostRecent && !mostRecent.hasUserMessage) {
        return yield self.openChat(mostRecent.id)
      }
      return yield self.createChat()
    }),

    // ---- Recents helpers -----------------------------------------------
    touchChatMetadata(chatId, patch = {}) {
      const now = new Date().toISOString()
      const existing = self.chatMetadatas.find(c => c.id === chatId)
      if (existing) {
        self.chatMetadatas = self.chatMetadatas.map(c =>
          c.id === chatId ? { ...c, mostRecentMessageTimestamp: now, ...patch } : c
        )
      } else {
        self.chatMetadatas = [
          { id: chatId, mostRecentMessageTimestamp: now, ...patch },
          ...self.chatMetadatas,
        ]
      }
    },

    toggleHeyNicSidebar() {
      self.heyNicSidebarExpanded = !self.heyNicSidebarExpanded
    },
    setHeyNicSidebar(value) {
      self.heyNicSidebarExpanded = value
    },
    setResponseLoading(value) {
      self.responseLoading = value
    },

    // ---- step-by-step advice panel -------------------------------------
    setAdvicePanelOpen(value) {
      self.advicePanelOpen = value
    },
    toggleAdvicePanel() {
      self.advicePanelOpen = !self.advicePanelOpen
    },

    // Drop any step pointing at this chat, so the step starts a fresh one on its
    // next interaction instead of reopening something that's gone.
    forgetAdviceChat(chatId) {
      if (chatId == null) return
      const stale = [...self.adviceChatIds.keys()].filter(
        step => self.adviceChatIds.get(step) === chatId
      )
      stale.forEach(step => self.adviceChatIds.delete(step))
    },

    /*
     * Load a step's saved chat, tolerating an id the server no longer has.
     *
     * `adviceChatIds` is a pointer into the shared Recents list, so it can be
     * invalidated behind our back: deleted from another tab or device, or expired
     * server-side. (A delete in THIS session is handled eagerly by deleteChat, but
     * that can't cover the rest.) A missing chat forgets its id and returns null,
     * which reads as "this step has no chat yet" everywhere upstream — so the next
     * pill tap creates one. Any other failure propagates: a network blip must not
     * silently discard a real transcript.
     */
    reopenAdviceChat: flow(function* (chatId) {
      try {
        return yield self.openChat(chatId)
      } catch (err) {
        if (!isMissingChat(err)) throw err
        console.warn(`[HeyNic] advice chat ${chatId} is gone; starting a new one`)
        self.forgetAdviceChat(chatId)
        return null
      }
    }),

    /*
     * Load the chat saved for an SBSA step into `activeChat`.
     *
     * Each step's transcript is separate, and only steps the user has engaged
     * with have one at all. There is one `activeChat` slot (and one stream) for
     * the whole app, so switching steps re-fetches — a GET only on the steps the
     * user has actually chatted on.
     *
     * The panel never reads `activeChat` directly (see `adviceChatFor`), so this
     * only has to LOAD; it never has to clear the slot to hide someone else's
     * transcript. That in turn means it can leave the standalone chat alone.
     */
    openAdviceStepChat: flow(function* (stepPath) {
      // This step's own response is still streaming — leave it exactly as is.
      if (self.isAdviceStepStreaming(stepPath)) return self.activeChat

      /*
       * A stream is running that is NOT this step's: the standalone chat's (the
       * user exited /hey-nic mid-stream, which deliberately keeps it running) or
       * another step's. openChat/createChat both call closeStream, so loading now
       * would abort that reply and lose it. Load nothing instead; the panel shows
       * its opening comment alone, and AdviceStepChat retries when the stream
       * ends.
       */
      if (self.responseLoading) return null

      const chatId = self.adviceChatIds.get(stepPath) ?? null
      // Nothing saved for this step yet: no fetch, and no reason to disturb
      // whatever else is loaded.
      if (chatId == null) return null
      if (self.activeChat?.id === chatId) return self.activeChat

      // Drop the outgoing chat before the fetch so nothing renders a stale
      // transcript against this step while its own is in flight.
      self.activeChat = null
      return yield self.reopenAdviceChat(chatId)
    }),

    /*
     * Send from an SBSA step's panel, creating that step's chat on the first
     * interaction. `title` names it in Recents (the first pill tap would
     * otherwise title the chat "Yes").
     */
    sendAdviceMessage: flow(function* (stepPath, text, title) {
      const trimmed = (text || '').trim()
      // One stream at a time, whichever surface owns it.
      if (!trimmed || self.responseLoading) return null

      let chatId = self.adviceChatIds.get(stepPath) ?? null

      // The step's chat isn't the one loaded (e.g. a foreign stream was running
      // when the panel mounted) — pull it in before sending. A stale id forgets
      // itself and falls through to create, rather than leaving the pill dead.
      if (chatId != null && self.activeChat?.id !== chatId) {
        const reopened = yield self.reopenAdviceChat(chatId)
        if (reopened == null) chatId = null
      }

      if (chatId == null) {
        const created = yield self.createChat()
        if (created?.id == null) return null
        self.adviceChatIds.set(stepPath, created.id)
      }

      return self.sendMessage(trimmed, { title, adviceStepPath: stepPath })
    }),

    // ---- Home screen chat ------------------------------------------------

    // The Home panel's chat is gone (deleted from Recents here or elsewhere):
    // drop the pointer so the panel starts a fresh one on the next send.
    forgetHomeChat(chatId) {
      if (chatId != null && self.homeChatId === chatId) self.homeChatId = null
    },

    /*
     * Load the Home panel's saved chat into `activeChat`, if it has one.
     *
     * Mirrors openAdviceStepChat: a foreign stream must not be aborted (openChat
     * calls closeStream), and a chat the server no longer has forgets itself
     * rather than leaving the panel pointing at nothing.
     */
    openHomeChat: flow(function* () {
      // The panel's own response is still streaming — leave it exactly as is.
      if (self.homeStreaming) return self.activeChat
      // Someone else's stream is running; loading now would abort their reply.
      // The panel shows its opening comment alone and retries when it ends.
      if (self.responseLoading) return null

      const chatId = self.homeChatId
      if (chatId == null) return null
      if (self.activeChat?.id === chatId) return self.activeChat

      self.activeChat = null
      try {
        return yield self.openChat(chatId)
      } catch (err) {
        if (!isMissingChat(err)) throw err
        console.warn(`[HeyNic] home chat ${chatId} is gone; starting a new one`)
        self.forgetHomeChat(chatId)
        return null
      }
    }),

    /*
     * Send from the Home panel, creating its chat on the first interaction.
     * `HOME_CHAT_TITLE` names it in Recents rather than letting the first pill
     * tap title the chat after itself.
     */
    sendHomeMessage: flow(function* (text) {
      const trimmed = (text || '').trim()
      // One stream at a time, whichever surface owns it.
      if (!trimmed || self.responseLoading) return null

      // The panel's chat isn't the one loaded (e.g. a foreign stream was running
      // when it mounted, or the standalone route swapped the slot) — pull it in
      // before sending. A stale id forgets itself and falls through to create.
      if (self.homeChatId != null && self.activeChat?.id !== self.homeChatId) {
        yield self.openHomeChat()
      }

      if (self.homeChatId == null || self.activeChat?.id !== self.homeChatId) {
        const created = yield self.createChat()
        if (created?.id == null) return null
        self.homeChatId = created.id
      }

      return self.sendMessage(trimmed, { title: HOME_CHAT_TITLE, homeStream: true })
    }),

    /*
     * ---- send + stream -------------------------------------------------
     * Optimistically add the user's message + a loading bot placeholder, then
     * open the streamed run. Returns the bot placeholder.
     *
     * Options:
     *   title          — name the chat this instead of its first message (the SBSA
     *                    panel names each chat after its step).
     *   adviceStepPath — the SBSA step this send belongs to. Recorded for the
     *                    duration of the stream so the advice flow can tell its
     *                    own reply from the standalone chat's; omitted (null) for
     *                    every send from /hey-nic.
     *   homeStream     — this send came from the Home screen panel. Same purpose
     *                    as adviceStepPath, for the Home surface.
     */
    sendMessage(text, { title, adviceStepPath = null, homeStream = false } = {}) {
      const trimmed = (text || '').trim()
      if (!trimmed || !self.activeChat || self.responseLoading) return null

      const chatId = self.activeChat.id
      const isFirstMessage = self.activeChat.isBlank
      const now = new Date().toISOString()

      self.activeChat.addMessage({ role: ROLE.USER, text: trimmed, createdTimestamp: now })
      const botMessage = self.activeChat.addMessage({
        role: ROLE.ASSISTANT,
        text: '',
        createdTimestamp: now,
        loading: true,
        streaming: true,
      })

      // First question titles the chat and jumps it to the top of Recents.
      if (isFirstMessage) {
        const chatTitle = title || trimmed
        self.activeChat.setTitle(chatTitle)
        self.touchChatMetadata(chatId, { title: chatTitle, hasUserMessage: true })
        if (chatId) self.updateTitle(chatId, chatTitle)
      } else {
        self.touchChatMetadata(chatId, { hasUserMessage: true })
      }

      // Whose stream this is, for as long as it runs (cleared by endStream /
      // closeStream).
      self.adviceStreamStepPath = adviceStepPath
      self.homeStreamActive = homeStream
      self.setResponseLoading(true)
      self.streamRun(trimmed, botMessage)
      return botMessage
    },

    streamRun: flow(function* (input, botMessage) {
      const chatId = self.activeChat?.id
      const controller = new AbortController()
      self.streamController = controller
      self.streamBuffer = ''
      self.streamEvent = null
      self.streamGotFinal = false

      try {
        const res = yield fetch(`${API_URL}agents/run/stream`, {
          method: 'POST',
          credentials: 'include',
          headers: buildStreamHeaders(),
          body: JSON.stringify({ chatId, input }),
          signal: controller.signal,
        })

        if (res.status === 422 || res.status === 400) {
          botMessage.markError(CONTENT_FILTER_MESSAGE)
          self.endStream()
          return
        }
        if (!res.ok || !res.body) throw new Error(`Stream failed: ${res.status}`)

        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        while (true) {
          const { value, done } = yield reader.read()
          if (done) break
          self.applyStreamChunk(botMessage, decoder.decode(value, { stream: true }))
        }
        // Flush any trailing buffered frame that didn't end in a newline.
        self.flushStreamBuffer(botMessage)

        // An error event during the stream already marked the message.
        if (botMessage.isErrorMessage) {
          self.endStream()
          return
        }
        // `final` is the only successful terminator; anything else means the
        // server cut out early, so surface a generic error.
        if (!self.streamGotFinal) {
          botMessage.markError(GENERIC_ERROR)
          self.endStream()
          return
        }

        botMessage.finish()
        self.endStream()
      } catch (err) {
        if (controller.signal.aborted) return
        botMessage.markError(GENERIC_ERROR)
        self.endStream()
        return
      }

      // Post-stream reconcile (authoritative ids/suggestions/title). Best-effort
      // and reconcile-only: never replace activeChat here, and bail if the user
      // has since switched chats — otherwise the reload could snap the UI back
      // to the old chat. A content-filtered turn is read-only, so skip it.
      if (botMessage.isErrorMessage) return
      if (!chatId || !self.activeChat || self.activeChat.id !== chatId) return
      try {
        const res = yield API.get(`agents/chat/${chatId}`)
        // The user may have switched during the reload; only apply in place if
        // they're still on the streamed chat.
        if (self.activeChat && self.activeChat.id === chatId) {
          self.activeChat.reconcileFromServer(mapChatFromApi(res.data))
        }
        yield self.getChatMetadatas()
      } catch (err) {
        console.error('[HeyNic] post-stream reconcile failed; keeping streamed response:', err)
      }
    }),

    // Buffer text and process whole lines as they complete (SSE frames may be
    // split across network chunks); the final partial line flushes at stream end.
    applyStreamChunk(botMessage, chunk) {
      if (!chunk) return
      self.streamBuffer += chunk
      const lines = self.streamBuffer.split(/\r?\n/)
      self.streamBuffer = lines.pop() ?? ''
      lines.forEach(line => self.processStreamLine(botMessage, line))
    },

    flushStreamBuffer(botMessage) {
      if (self.streamBuffer.trim()) self.processStreamLine(botMessage, self.streamBuffer)
      self.streamBuffer = ''
    },

    processStreamLine(botMessage, line) {
      // Once errored, ignore the rest of the stream.
      if (botMessage.isErrorMessage) return

      const trimmed = line.trim()
      // A blank line ends the current SSE frame.
      if (!trimmed) {
        self.streamEvent = null
        return
      }
      if (trimmed.startsWith('id:')) return
      if (trimmed.startsWith('event:')) {
        self.streamEvent = trimmed.slice(6).trim()
        const errorCopy = ERROR_EVENT_MESSAGES[self.streamEvent]
        if (errorCopy) botMessage.markError(errorCopy)
        else if (self.streamEvent === FINAL_EVENT) self.streamGotFinal = true
        return
      }

      // Only `data:` lines carry the JSON payload; tolerate a bare JSON object.
      let payload
      if (trimmed.startsWith('data:')) payload = trimmed.slice(5).trim()
      else if (trimmed.startsWith('{')) payload = trimmed
      else return
      if (!payload || payload === '[DONE]') return

      try {
        self.applyBotResponse(botMessage, JSON.parse(payload))
      } catch (err) {
        // Ignore a malformed/partial frame rather than dumping raw text.
      }
    },

    // Applies one BotResponse frame to the streaming message. `choices` is at the
    // top level of the frame; older/alternate shapes nest it under
    // `chatCompletions`, so accept either.
    applyBotResponse(botMessage, response) {
      const choices = response?.choices ?? response?.chatCompletions?.choices ?? []
      choices.forEach(choice => {
        const delta = choice?.delta ?? {}
        if (delta.content) botMessage.appendText(delta.content)
        if (delta.statusUpdate) botMessage.applyStatusUpdate(delta.statusUpdate)
        if (delta.suggestions) botMessage.setSuggestions(delta.suggestions)
      })
    },

    endStream() {
      self.setResponseLoading(false)
      self.adviceStreamStepPath = null
      self.homeStreamActive = false
      if (self.streamController) {
        self.streamController = null
      }
    },

    closeStream() {
      if (self.streamController) {
        self.streamController.abort()
        self.streamController = null
      }
      self.setResponseLoading(false)
      self.adviceStreamStepPath = null
      self.homeStreamActive = false
    },

    beforeDestroy() {
      self.closeStream()
    },
  }))

// A chat id the server no longer has: deleted elsewhere, or expired. Anything
// else (network, 5xx) is a transient failure and must not discard the id.
function isMissingChat(err) {
  const status = err?.response?.status
  return status === 404 || status === 410
}

// XSRF + timestamp headers matching the axios interceptors, for raw fetch.
function buildStreamHeaders() {
  const headers = {
    'Content-Type': 'application/json',
    // Declare we want the SSE stream; some proxies use this to disable buffering.
    'Accept': 'text/event-stream',
    'X-Request-Timestamp': Math.floor(Date.now() / 1000).toString(),
  }
  const xsrf = Cookies.get('XSRF-TOKEN')
  if (xsrf) headers['X-XSRF-TOKEN'] = xsrf
  return headers
}

function mapMessageFromApi(m = {}) {
  return {
    id: m.messageId ?? null,
    role: m.role ?? ROLE.ASSISTANT,
    text: m.text ?? '',
    createdTimestamp: m.createdTimestamp ?? '',
    toolName: m.toolName ?? null,
    toolCallId: m.toolCallId ?? null,
    validationAgent: !!m.validationAgent,
    suggestions: (m.suggestions || []).map(s => s?.text ?? '').filter(Boolean),
    navLinkText: m.navLink?.text ?? '',
  }
}

function mapChatFromApi(data = {}) {
  return {
    id: data.chatId ?? null,
    title: data.title ?? '',
    createdTimestamp: data.createdTimestamp ?? '',
    messages: (data.messages || []).map(mapMessageFromApi),
  }
}

// The list endpoint returns full chats; derive the lightweight Recents entry.
function mapChatToMetadata(chat = {}) {
  const messages = chat.messages || []
  const last = messages[messages.length - 1]
  return {
    id: chat.chatId,
    title: chat.title || '',
    createdAt: chat.createdTimestamp || '',
    mostRecentMessageTimestamp: last?.createdTimestamp || chat.createdTimestamp || '',
    hasUserMessage: messages.some(m => m.role === ROLE.USER),
    messageCount: messages.length,
  }
}

export default ChatStore
export { ROLE }
